import { travelTimeMinutes } from '../utils/travelTimeOptions';
import React, { useState, useEffect } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { Stop, StopDetailedView } from '../types';
import DialogModal from '../../../components/DialogModal';
import DropdownSelect, {
  DropdownSelectOption
} from '../../../components/DropdownSelect/DropdownSelect';
import { SnackbarSeverityType } from '../../../enums';
import { isEmpty } from 'lodash';
import { updateStop } from '../../../apis/transport/stops';
import TextInputWithIcon from '../../../components/TextInputWithIcon';

interface EditStopDialogProps {
  open: boolean;
  onClose: () => void;
  onEditStop: () => void;
  currentStop: StopDetailedView;
  stops: Stop[];
  showToast: (message: string, severity: SnackbarSeverityType) => void;
}

const EditStopDialog: React.FC<EditStopDialogProps> = ({
  open,
  onClose,
  onEditStop,
  currentStop,
  stops,
  showToast
}) => {
  const [name, setName] = useState('');
  const [travelTime, setTravelTime] = useState('');
  const [mapsPin, setMapsPin] = useState('');
  const [offsetPin, setOffsetPin] = useState('');
  const [isFirstStop, setIsFirstStop] = useState(false);
  const [triggerStopId, setTriggerStopId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [triggerStopOptions, setTriggerStopOptions] = useState<
    DropdownSelectOption[]
  >([]);

  const travelTimeOptions: DropdownSelectOption[] = travelTimeMinutes.map(
    (minute) => ({
      value: `${minute}`,
      label: `${minute} min`
    })
  );

  useEffect(() => {
    if (currentStop) {
      setName(currentStop.name);
      setTravelTime(String(currentStop.travelTime));
      if (currentStop.location) {
        setMapsPin(`${currentStop.location.lat}, ${currentStop.location.long}`);
      }
      if (currentStop.offsetLocation) {
        setOffsetPin(
          `${currentStop.offsetLocation.lat}, ${currentStop.offsetLocation.long}`
        );
      }
      setIsFirstStop(currentStop.stopNumber === 1);
      setTriggerStopId(currentStop.triggerStop || null);
    }
  }, [open, currentStop]);

  useEffect(() => {
    if (isFirstStop) {
      setTriggerStopOptions([]);
    } else {
      const filteredStops = stops.filter(
        (stop) => stop.stopNumber < currentStop.stopNumber
      );
      const filteredOptions = filteredStops.map((stop) => ({
        value: stop.id,
        label: stop.name
      }));
      setTriggerStopOptions(filteredOptions);
    }
  }, [isFirstStop, stops, currentStop]);

  const handleEditStop = async () => {
    setIsLoading(true);
    try {
      await updateStop(currentStop.id, {
        name,
        travelTime: !isEmpty(travelTime) ? travelTime : undefined,
        location: !isEmpty(mapsPin)
          ? {
              lat: mapsPin.split(',')[0]?.trim(),
              long: mapsPin.split(',')[1]?.trim()
            }
          : undefined,
        offsetLocation: !isEmpty(offsetPin)
          ? {
              lat: offsetPin.split(',')[0]?.trim(),
              long: offsetPin.split(',')[1]?.trim()
            }
          : undefined,
        triggerStop: !isEmpty(triggerStopId)
          ? (triggerStopId as unknown as string)
          : undefined
      });
      showToast('Stop updated successfully!', SnackbarSeverityType.success);
      onEditStop();
      onClose();
    } catch (error) {
      showToast('Failed to update stop', SnackbarSeverityType.error);
    } finally {
      setIsLoading(false);
    }
  };

  const isEditButtonDisabled = !name || !mapsPin || isLoading;

  return (
    <DialogModal
      open={open}
      onClose={onClose}
      title='Update stop details'
      actions={
        <>
          <Button onClick={onClose} color='primary' variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleEditStop}
            disabled={isEditButtonDisabled}
            variant='contained'
          >
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Submit'
            )}
          </Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <TextInputWithIcon
          label='Stop Number'
          placeholder='Enter Stop Number'
          value={currentStop.stopNumber}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
          disabled
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextInputWithIcon
            label='Stop Name'
            placeholder='Enter Stop Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <DropdownSelect
            label={isFirstStop ? 'Travel Time' : 'Travel Time'}
            name='travelTime'
            placeholder='Select Travel Time'
            value={travelTime}
            options={travelTimeOptions}
            onChange={(e) => setTravelTime(e.target.value as string)}
            required
            hasTopLabel
            disabled={isFirstStop}
          />
        </Box>
        <DropdownSelect
          label={
            isFirstStop ? 'Trigger to this Stop' : 'Trigger to this Stop'
          }
          name='triggerStopId'
          placeholder='Select Stop'
          value={triggerStopId || ''}
          options={triggerStopOptions}
          onChange={(e) => setTriggerStopId(e.target.value as string)}
          required
          hasTopLabel
          disabled={isFirstStop}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextInputWithIcon
            label='Google Maps Pin'
            value={mapsPin}
            onChange={(e) => setMapsPin(e.target.value)}
            placeholder='19.1831, 72.8356'
            required
            fullWidth
          />
          <TextInputWithIcon
            label='Offset Location Pin'
            value={offsetPin}
            onChange={(e) => {
              setOffsetPin(e.target.value);
            }}
            placeholder='19.1831, 72.8356'
            fullWidth
          />
        </Box>
      </Box>
    </DialogModal>
  );
};

export default EditStopDialog;
